import '../../styles/tile-select.css'
import {useEffect, useRef, useState} from "react";
import {Button, Checkbox, Form, InputNumber, Popover, Tabs} from "antd";
import AppColor from "../../components/AppColor";
import {AppActions, Bounding, FlowArrow, TerrainLayer, useAppContext} from "../../context/AppContext";
import {
    MinusOutlined,
    PlusOutlined,
    RadiusBottomleftOutlined,
    RadiusBottomrightOutlined,
    RadiusUpleftOutlined,
    RadiusUprightOutlined
} from "@ant-design/icons";
import ShapeTable from "../map/ShapeTable";
import {useModal} from "../../context/ModalContext";
import ShapeFileHelper from "../../helpers/ShapeFileHelper";
import {v4} from "uuid";
import ResultHeaderCell from "../../components/table/ResultHeaderCell";

export const Layers = [
    'Mannings n',
    'Bank Station',
    'Ineffective Area',
    'Blocked Obstruction',
    'Multiple Opening',
    'Flood Extent',
    'Flow Direction',
    'Flow Change Location',
    'Flow Path',
    'Boundary Condition',
]

const ReferenceLayers = [
    {name: 'US Counties Light', file: 'cb_2018_us_county_20m.kml'},
    {name: 'US Counties HD', file: 'cb_2018_us_county_500k.kml'},
    {name: 'NC FIRM Panel Effective', file: 'nc_firm.kml'},
    {name: 'NC Political Areas', file: 'political.kml'},
    {name: 'NCDOT Bridge Structures', file: 'bridge_structures.kml'},
]

const getLayers = (context, type, layerName) => {
    if(context && context[type] && context[type]?.selectedLayers) {
        return context[type]?.selectedLayers[layerName]
    }

    return []
}

export default function LayerSelect({type, projectLayers, onUpdate, onProjectLayerChange, riverBounding}) {
    const {mapContext, dispatchProject, appContext, getSelectedHistory, getMapData} = useAppContext()
    const [modelLayers, setModelLayers] = useState([...getLayers(mapContext, type, 'model')])
    const [referenceLayers, setReferenceLayers] = useState([...getLayers(mapContext, type, 'reference')])
    const [projLayers, setProjLayers] = useState([...getLayers(mapContext, type, 'project')])
    const formRef = useRef()
    const {modal} = useModal()
    const [modelPopoverOpen, setModelPopoverOpen] = useState(false)
    const [referencePopoverOpen, setReferencePopoverOpen] = useState(false)
    const [projectPopoverOpen, setProjectPopoverOpen] = useState(false)
    const [bounding, setBounding] = useState([])
    const debounceRef = useRef(null)

    useEffect(() => {
        update()
    }, [modelLayers, referenceLayers, projLayers])

    const update = () => {
        if(debounceRef.current)
            clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            const bounding = formRef?.current?.getFieldsValue() || Bounding
            const flowArrow = formRef?.current?.getFieldsValue() || FlowArrow
            const terrainLayers = formRef?.current?.getFieldsValue() || TerrainLayer

            dispatchProject({
                type: AppActions.SET_MAP_CONTEXT,
                data: {
                    map: {
                        ...mapContext,
                        [type]: {
                            ...mapContext[type],
                            update: v4(),
                            bounding: {
                                showBounding: bounding?.showBounding,
                                northWest: bounding?.northWest,
                                northEast: bounding?.northEast,
                                southWest: bounding?.southWest,
                                southEast: bounding?.southEast,
                            },
                            flowArrow: {
                                flow_count: flowArrow?.flow_count,
                                flow_size: flowArrow?.flow_count,
                                flow_change_size: flowArrow?.flow_change_size,
                                boundary_size: flowArrow?.boundary_size,
                            },
                            selectedLayers: {
                                model: [...modelLayers],
                                reference: [...referenceLayers],
                                project: [...projLayers],
                            },
                            terrainLayers: {
                                resolution_size: terrainLayers?.resolution_size,
                                opacity_size: terrainLayers?.opacity_size,
                                gray_opacity_size: terrainLayers?.gray_opacity_size,
                                gray_z_factor: terrainLayers?.gray_z_factor,
                                azimuth: terrainLayers?.azimuth,
                                altitude: terrainLayers?.altitude,
                            }
                        }
                    }
                }
            })
        }, 500)
    }

    useEffect(() => {
        (async () => {
            try {
                if (mapContext[type]) {
                    const _b = await ShapeFileHelper.getBounding(riverBounding || [], mapContext[type]?.bounding || {})
                    setBounding(_b)
                }
            } catch (error) {
                console.error('Error getting bounding:', error)
                setBounding([])
            }
        })()
    }, [mapContext[type]?.bounding, riverBounding, type]);

    const closeAllPopovers = () => {
        setModelPopoverOpen(false)
        setReferencePopoverOpen(false)
        setProjectPopoverOpen(false)
    }

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
            {/* Model Layers Button */}
            <Popover
                placement={'rightTop'}
                trigger={'click'}
                open={modelPopoverOpen && !modal.open}
                onOpenChange={(visible) => {
                    if(!modal.open) {
                        closeAllPopovers()
                        setModelPopoverOpen(visible)
                    }
                }}
                overlayStyle={{padding: 0}}
                content={(
                    <div style={{minWidth: 250}}>
                        <Tabs>
                            <Tabs.TabPane tab={'Model Layers'} key={'ModelLayers'}>
                                <div style={{padding: 8}}>
                                    <table>
                                        <tbody>
                                        {
                                            Layers.map(x => (
                                                <tr key={x}>
                                                    <td>
                                                        <Checkbox
                                                            checked={modelLayers.includes(x)} value={x}
                                                            style={{paddingLeft: 0}}
                                                            onChange={(e) => {
                                                                if (!e.target.checked) {
                                                                    setModelLayers(modelLayers.filter(y => y !== x))
                                                                } else {
                                                                    modelLayers.push(x)
                                                                    setModelLayers([...modelLayers])
                                                                }
                                                            }}
                                                        >
                                                            {x}
                                                        </Checkbox>
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                        </tbody>
                                    </table>
                                </div>
                            </Tabs.TabPane>
                            <Tabs.TabPane tab={'Settings'} key={'Layer Settings'}>
                                <Form
                                    labelAlign={'left'}
                                    size={'small'}
                                    layout={'horizontal'}
                                    ref={formRef}
                                    onChange={update}
                                    initialValues={{
                                        ...(mapContext[type]?.bounding),
                                        ...(mapContext[type]?.flowArrow),
                                        ...(mapContext[type]?.terrainLayers),
                                    }}
                                >
                                    <div style={{display: 'flex', alignItems: 'start', alignContent: 'start', gap: 12}}>
                                        <table style={{}}>
                                            <tbody>
                                            <tr>
                                                <ResultHeaderCell colSpan={5}>
                                                    Bounding Settings
                                                </ResultHeaderCell>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <RadiusUpleftOutlined/>
                                                </td>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0}}
                                                        name={'northWest'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                                <td>
                                                    <RadiusUprightOutlined/>
                                                </td>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0}}
                                                        name={'northEast'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                                <td>
                                                    <Button size={'small'} icon={<PlusOutlined/>}
                                                            onClick={() => {
                                                                const formData = formRef.current.getFieldsValue()
                                                                formRef.current.setFieldsValue({
                                                                    ...formData,
                                                                    northWest: formData.northWest + 25,
                                                                    northEast: formData.northEast + 25,
                                                                    southWest: formData.southWest + 25,
                                                                    southEast: formData.southEast + 25,
                                                                })
                                                                update()
                                                            }}/>

                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <RadiusBottomleftOutlined/>
                                                </td>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0}}
                                                        name={'southWest'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                                <td>
                                                    <RadiusBottomrightOutlined/>
                                                </td>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0}}
                                                        name={'southEast'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                                <td>
                                                    <Button.Group>
                                                        <Button size={'small'} icon={<MinusOutlined/>}
                                                                onClick={() => {
                                                                    const formData = formRef.current.getFieldsValue()
                                                                    formRef.current.setFieldsValue({
                                                                        ...formData,
                                                                        northWest: formData.northWest - 25,
                                                                        northEast: formData.northEast - 25,
                                                                        southWest: formData.southWest - 25,
                                                                        southEast: formData.southEast - 25,
                                                                    })
                                                                    update()
                                                                }}/>
                                                    </Button.Group>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan={5}>
                                                    <div
                                                        style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                                        <div style={{marginLeft: 4}}>
                                                            Show/Hide Bounding:
                                                        </div>
                                                        <Form.Item
                                                            style={{padding: 0, margin: 0}}
                                                            name={'showBounding'}
                                                            valuePropName={'checked'}
                                                        >
                                                            <Checkbox/>
                                                        </Form.Item>
                                                    </div>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        <table>
                                            <tbody>
                                            <tr>
                                                <ResultHeaderCell>
                                                    Flow Arrow Settings
                                                </ResultHeaderCell>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                        label={(<div style={{width: 125}}>Count</div>)}
                                                        name={'flow_count'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                        label={(<div style={{width: 125}}>Size</div>)}
                                                        name={'flow_size'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        <table>
                                            <tbody>
                                            <tr>
                                                <ResultHeaderCell>
                                                    Other Settings
                                                </ResultHeaderCell>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                        label={(<div style={{width: 125}}>Flow Change Size</div>)}
                                                        name={'flow_change_size'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <Form.Item
                                                        style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                        label={(<div style={{width: 125}}>Boundary Size</div>)}
                                                        name={'boundary_size'}
                                                    >
                                                        <InputNumber/>
                                                    </Form.Item>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>

                                    </div>
                                </Form>
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
                )}
            >
                <Button
                    size={'small'}
                    style={{padding: 5, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                    onClick={() => {
                        closeAllPopovers()
                        setModelPopoverOpen(!modelPopoverOpen)
                    }}
                    title="Model Layers"
                >
                    <img src="/icons/Model Layers.svg" alt="Model Layers" style={{width: 25, height: 25}} />
                </Button>
            </Popover>

            {/* Reference Layers Button */}
            <Popover
                placement={'rightTop'}
                trigger={'click'}
                open={referencePopoverOpen && !modal.open}
                onOpenChange={(visible) => {
                    if(!modal.open) {
                        closeAllPopovers()
                        setReferencePopoverOpen(visible)
                    }
                }}
                overlayStyle={{padding: 0}}
                content={(
                    <div style={{minWidth: 250, padding: 8}}>
                        <div style={{backgroundColor: AppColor.secondColor, paddingLeft: 4, marginBottom: 8}}>
                            Reference Layers
                        </div>
                        <table>
                            <tbody>
                            {
                                ReferenceLayers.map(x => (
                                    <tr key={x.name}>
                                        <td>
                                            <Checkbox
                                                checked={referenceLayers.find(y => y.name === x.name)}
                                                value={x.name}
                                                style={{paddingLeft: 0}}
                                                onChange={(e) => {
                                                    if (!e.target.checked) {
                                                        setReferenceLayers(referenceLayers.filter(y => y.name !== x.name))
                                                    } else {
                                                        referenceLayers.push(x)
                                                        setReferenceLayers([...referenceLayers])
                                                    }
                                                }}
                                            >
                                                {x.name}
                                            </Checkbox>
                                        </td>
                                    </tr>
                                ))
                            }
                            </tbody>
                        </table>
                    </div>
                )}
            >
                <Button
                    size={'small'}
                    style={{padding: 5, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                    onClick={() => {
                        closeAllPopovers()
                        setReferencePopoverOpen(!referencePopoverOpen)
                    }}
                    title="Reference Layers"
                >
                    <img src="/icons/Reference Layers.svg" alt="Reference Layers" style={{width: 25, height: 25}} />
                </Button>
            </Popover>

            {/* Project Layers Button */}
            <Popover
                placement={'rightTop'}
                trigger={'click'}
                open={projectPopoverOpen && !modal.open}
                onOpenChange={(visible) => {
                    if(!modal.open) {
                        closeAllPopovers()
                        setProjectPopoverOpen(visible)
                    }
                }}
                overlayStyle={{padding: 0}}
                content={(
                    <div style={{minWidth: 400, padding: 8}}>
                        <Tabs type={'card'} size={'small'}>
                            <Tabs.TabPane tab={'Layers'} key={'Layers'}>
                                <ShapeTable
                                    selectable={true}
                                    bounding={bounding}
                                    shapes={projectLayers}
                                    selectedLayers={projLayers}
                                    onSelected={(layers) => {
                                        setProjLayers([...layers])
                                    }}
                                    onChange={onProjectLayerChange}
                                />
                            </Tabs.TabPane>
                            <Tabs.TabPane tab={'Raster Layer Settings'} key={'Raster Layer Settings'}>
                                <Form
                                    labelAlign={'left'}
                                    size={'small'}
                                    layout={'horizontal'}
                                    ref={formRef}
                                    onChange={update}
                                    initialValues={{
                                        ...(mapContext[type]?.terrainLayers),
                                    }}
                                >
                                    <table>
                                        <tbody>
                                        <tr>
                                            <td>
                                                <Form.Item
                                                    style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                    label={(<div style={{width: 125}}>Resolution Size</div>)}
                                                    name={'resolution_size'}
                                                >
                                                    <InputNumber min={0} max={512}/>
                                                </Form.Item>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Form.Item
                                                    style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                    label={(<div style={{width: 125}}>Opacity</div>)}
                                                    name={'opacity_size'}
                                                >
                                                    <InputNumber min={0} max={1} step={0.1}/>
                                                </Form.Item>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Form.Item
                                                    style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                    label={(<div style={{width: 125}}>Hillshade Opacity</div>)}
                                                    name={'gray_opacity_size'}
                                                >
                                                    <InputNumber min={0.1} max={1} step={0.1}/>
                                                </Form.Item>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Form.Item
                                                    style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                    label={(<div style={{width: 125}}>Z Factor</div>)}
                                                    name={'gray_z_factor'}
                                                >
                                                    <InputNumber min={0} max={10} step={0.1}/>
                                                </Form.Item>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Form.Item
                                                    style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                    label={(<div style={{width: 125}}>Azimuth</div>)}
                                                    name={'azimuth'}
                                                >
                                                    <InputNumber min={0} max={360} step={1}/>
                                                </Form.Item>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <Form.Item
                                                    style={{padding: 0, margin: 0, textAlign: 'start'}}
                                                    label={(<div style={{width: 125}}>Altitude</div>)}
                                                    name={'altitude'}
                                                >
                                                    <InputNumber min={0} max={90} step={1}/>
                                                </Form.Item>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </Form>
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
                )}
            >
                <Button
                    size={'small'}
                    style={{padding: 5, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                    onClick={() => {
                        closeAllPopovers()
                        setProjectPopoverOpen(!projectPopoverOpen)
                    }}
                    title="Project Layers"
                >
                    <img src="/icons/Project Layers.svg" alt="Project Layers" style={{width: 25, height: 25}} />
                </Button>
            </Popover>
        </div>
    )
}